import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Glossary/product data is large — load it only on routes that need the names
// (those pages import the same modules, so the chunk is already in flight).
const loadSlugNames = {
  glossary: () => import('../data/glossaryTerms').then(({ GLOSSARY_TERMS }) =>
    Object.fromEntries(GLOSSARY_TERMS.map((t) => [t.slug, t.shortName || t.term]))),
  products: () => import('../data/products').then(({ PRODUCTS }) =>
    Object.fromEntries(PRODUCTS.map((p) => [p.slug, p.shortName || p.name]))),
};

const routeNames = {
  '': 'Home',
  'products': 'Products',
  'pricing': 'Pricing',
  'standards': 'Standards',
  'dora': 'DORA Compliance',
  'blog': 'Blog',
  'ventures': 'Ventures',
  'portfolio': 'Portfolio',
  'careers': 'Careers',
  'quote': 'Request a Quote',
  'terms': 'Terms',
  'privacy': 'Privacy Policy',
  'cookie-policy': 'Cookie Policy',
  'imprint': 'Imprint',
  'helpdesk': 'Helpdesk',
  'tools': 'Tools',
  'html-to-image': 'HTML to Image',
  'screenshot-to-image': 'Screenshot to Image',
  'pdf-tools': 'PDF Tools',
  'security-tools': 'Security Scanner',
  'blockchain-compliance-checker': 'Blockchain Compliance Checker',
  'reaching-new-frontiers': 'Reaching New Frontiers',
  'meridian-2024-highlights': 'Meridian 2024 Highlights',
  'ebsi-verifiable-credentials': 'EBSI Verifiable Credentials',
  'how-blockchain-standards-enable-enterprises-to-reach-global-customers': 'Blockchain Standards for Global Customers',
  'digital-payments-future': "Europe's Digital Payment Future",
  'estandares-sismicos-colombia': 'Estándares Sísmicos Colombia',
  'glossary': 'Glossary',
};

function Breadcrumbs() {
  const location = useLocation();
  const path = location.pathname;
  const section = path.split('/').filter(Boolean)[0];
  const [slugNames, setSlugNames] = useState({});

  useEffect(() => {
    if (!loadSlugNames[section] || slugNames[section]) return;
    loadSlugNames[section]().then((names) => setSlugNames((prev) => ({ ...prev, [section]: names })));
  }, [section, slugNames]);

  if (path === '/') return null;

  const segments = path.split('/').filter(Boolean);
  const dynamicNames = slugNames[section] || {};
  const crumbs = [{ name: 'Home', path: '/' }];
  let currentPath = '';

  segments.forEach((segment) => {
    currentPath += '/' + segment;
    const name = routeNames[segment] || dynamicNames[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
    crumbs.push({ name, path: currentPath + '/' });
  });

  return (
      <nav aria-label="Breadcrumb" className="relative max-w-6xl mx-auto px-4 sm:px-6" style={{ paddingTop: '88px' }}>
        <ol className="flex flex-wrap items-center text-sm text-gray-500 pb-2">
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <li key={crumb.path} className="flex items-center">
                {index > 0 && (
                  <span className="mx-2 text-gray-600" aria-hidden="true">/</span>
                )}
                {isLast ? (
                  <span className="text-gray-400" aria-current="page">{crumb.name}</span>
                ) : (
                  <Link to={crumb.path} className="text-purple-600 hover:text-purple-400 transition duration-150 ease-in-out">
                    {crumb.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
  );
}

export default Breadcrumbs;
